import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthFacade } from '../auth/auth.facade';
import { APP_HOME_URL } from '../auth/auth.state';
import { OrganizationsFacade } from '../organizations/organizations.facade';
import { PlansFacade } from '../plans/plans.facade';
import { SELECT_PLAN_URL } from '../plans/plans.state';

/**
 * Resolves the organization id, loading it if the session hasn't yet. `null`
 * when it can't be determined — the guards below then let the navigation
 * through rather than stranding the user on a paywall we can't justify.
 *
 * Goes through `ensureActiveOrganization` rather than fetching the server's
 * current organization: this guard runs in parallel with `AuthGuard` (Angular
 * evaluates a route's `canActivate` guards concurrently), and a second source
 * of truth for `current` would race it and could bill the wrong organization.
 */
async function resolveOrganizationId(
  orgs: OrganizationsFacade,
): Promise<number | null> {
  const organization = await orgs.requireActiveOrganization();
  return organization?.id ?? null;
}

/**
 * Holds the app closed until the organization has a plan it can actually use —
 * see `PlansFacade.planBlockReason`, which covers both never having subscribed
 * and a trial that has run out.
 *
 * The subscription is fetched once per session (see
 * `PlansFacade.ensureSubscriptionLoaded`) and only those two definite states
 * redirect — a network or server failure leaves the user where they are, since
 * it says nothing about whether they have paid.
 *
 * Developers are exempt: their whole world is the API keys page, and they have
 * no way to buy anything.
 */
export const subscriptionGuard: CanActivateFn = async (): Promise<
  boolean | UrlTree
> => {
  const auth = inject(AuthFacade);
  const orgs = inject(OrganizationsFacade);
  const plans = inject(PlansFacade);
  const router = inject(Router);

  // AuthGuard owns the unauthenticated redirect; don't compete with it.
  if (!auth.isAuthenticated()) return true;
  if (auth.isDeveloper()) return true;

  const organizationId = await resolveOrganizationId(orgs);
  if (organizationId == null) return true;

  await plans.ensureSubscriptionLoaded(organizationId);

  return plans.requiresPlan() ? router.createUrlTree([SELECT_PLAN_URL]) : true;
};

/**
 * The inverse, guarding the paywall itself: an org that already has a usable
 * plan has no business on the plan picker, so it goes home. Mutually exclusive
 * with {@link subscriptionGuard}, so the two can't bounce a navigation between
 * each other.
 */
export const planSelectionGuard: CanActivateFn = async (): Promise<
  boolean | UrlTree
> => {
  const auth = inject(AuthFacade);
  const orgs = inject(OrganizationsFacade);
  const plans = inject(PlansFacade);
  const router = inject(Router);

  const organizationId = await resolveOrganizationId(orgs);
  if (organizationId == null) return true;

  await plans.ensureSubscriptionLoaded(organizationId);

  if (plans.requiresPlan()) return true;
  return router.createUrlTree([auth.resolveLandingUrl(APP_HOME_URL)]);
};
